import { all, call, put, takeLatest } from 'redux-saga/effects';
import { AxiosCall, AxiosPost, AxiosPatch, AxiosDelete } from '../../../utils/axios';
import * as actions from './recipeActions';

// Create Recipe
function* createRecipe(action) {
  try {
    const result = yield call(AxiosPost, '/mycore/recipe/add/', action.payload);
    yield put(actions.createRecipeSuccess(result.data));
  } catch (e) {
    yield put(actions.createRecipeFailure(e.response?.data?.error || e.message));
  }
}

// Update Recipe
function* updateRecipe(action) {
  try {
    const { id, data } = action.payload;
    const result = yield call(AxiosPatch, `/mycore/${id}/recipe/update/`, data);
    yield put(actions.updateRecipeSuccess(result.data));
  } catch (e) {
    yield put(actions.updateRecipeFailure(e.response?.data?.error || e.message));
  }
}

// Delete Recipe
function* deleteRecipe(action) {
  try {
    const id = action.payload;
    yield call(AxiosDelete, `/mycore/${id}/recipe/delete/`);
    yield put(actions.deleteRecipeSuccess(id));
  } catch (e) {
    yield put(actions.deleteRecipeFailure(e.response?.data?.error || e.message));
  }
}

// Get Recipes by Organization
function* getRecipesByOrg(action) {
  try {
    const organizationId = action.payload;
    const result = yield call(AxiosCall, `/mycore/${organizationId}/recipe/by-org/`);
    yield put(actions.getRecipesByOrgSuccess(result.data));
  } catch (e) {
    yield put(actions.getRecipesByOrgFailure(e.response?.data?.error || e.message));
  }
}

// Get My Recipes
function* getMyRecipes() {
  try {
    const result = yield call(AxiosCall, '/mycore/recipe/my/');
    yield put(actions.getMyRecipesSuccess(result.data));
  } catch (e) {
    yield put(actions.getMyRecipesFailure(e.response?.data?.error || e.message));
  }
}

// Get Public Recipes
function* getPublicRecipes() {
  try {
    const result = yield call(AxiosCall, '/mycore/recipe/public/');
    yield put(actions.getPublicRecipesSuccess(result.data));
  } catch (e) {
    yield put(actions.getPublicRecipesFailure(e.response?.data?.error || e.message));
  }
}

// Search Public Recipes with Filters
function* searchPublicRecipes(action) {
  try {
    const filters = action.payload;
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.type) params.append('type', filters.type);
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    if (filters.strain) params.append('strain', filters.strain);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);

    const result = yield call(AxiosCall, `/mycore/recipe/public/?${params.toString()}`);
    yield put(actions.searchPublicRecipesSuccess(result.data));
  } catch (e) {
    yield put(actions.searchPublicRecipesFailure(e.response?.data?.error || e.message));
  }
}

// Duplicate Recipe
function* duplicateRecipe(action) {
  try {
    const recipeId = action.payload;
    const result = yield call(AxiosPost, `/mycore/${recipeId}/recipe/duplicate/`, {});
    yield put(actions.duplicateRecipeSuccess(result.data));
  } catch (e) {
    yield put(actions.duplicateRecipeFailure(e.response?.data?.error || e.message));
  }
}

// Publish Recipe (toggle public/private)
function* publishRecipe(action) {
  try {
    const { recipeId, isPublic } = action.payload;
    const result = yield call(AxiosPatch, `/mycore/${recipeId}/recipe/update/`, { is_public: isPublic });
    yield put(actions.publishRecipeSuccess(result.data));
  } catch (e) {
    yield put(actions.publishRecipeFailure(e.response?.data?.error || e.message));
  }
}

// Like Recipe
function* likeRecipe(action) {
  try {
    const recipeId = action.payload;
    const result = yield call(AxiosPost, `/mycore/${recipeId}/recipe/like/`, {});
    yield put(actions.likeRecipeSuccess(result.data));
    // Refresh public recipes to show updated like count
    yield put(actions.getPublicRecipesRequest());
  } catch (e) {
    yield put(actions.likeRecipeFailure(e.response?.data?.error || e.message));
  }
}

// Share Recipe
function* shareRecipe(action) {
  try {
    const recipeId = action.payload;
    const result = yield call(AxiosCall, `/mycore/${recipeId}/recipe/share/`);
    yield put(actions.shareRecipeSuccess(result.data));

    // Copy to clipboard
    if (navigator.clipboard && result.data.share_url) {
      yield call([navigator.clipboard, 'writeText'], result.data.share_url);
    }
  } catch (e) {
    yield put(actions.shareRecipeFailure(e.response?.data?.error || e.message));
  }
}

// Root Saga
export default function* recipeSaga() {
  yield all([
    takeLatest(actions.CREATE_RECIPE.REQUEST, createRecipe),
    takeLatest(actions.UPDATE_RECIPE.REQUEST, updateRecipe),
    takeLatest(actions.DELETE_RECIPE.REQUEST, deleteRecipe),
    takeLatest(actions.GET_RECIPES_BY_ORG.REQUEST, getRecipesByOrg),
    takeLatest(actions.GET_MY_RECIPES.REQUEST, getMyRecipes),
    takeLatest(actions.GET_PUBLIC_RECIPES.REQUEST, getPublicRecipes),
    takeLatest(actions.DUPLICATE_RECIPE.REQUEST, duplicateRecipe),
    takeLatest(actions.PUBLISH_RECIPE.REQUEST, publishRecipe),
    takeLatest(actions.LIKE_RECIPE.REQUEST, likeRecipe),
    takeLatest(actions.SHARE_RECIPE.REQUEST, shareRecipe),
    takeLatest(actions.SEARCH_PUBLIC_RECIPES.REQUEST, searchPublicRecipes),
  ]);
}
